import React from 'react';
import { isAppsInFlexibleHCEnabled } from 'feature-flags';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { HelpCenterAnnouncement } from '@atlassian/help-center-announcement-element';
import EditorViewerComponent from '@atlassian/help-center-editor-element/src/ui/editor-view-renderer';
import { HeroElement } from '@atlassian/help-center-hero-element';
import { ConnectElement } from '@atlassian/help-layout-connect-element';
import { ForgeElement } from '@atlassian/help-layout-forge-element';
import { ListOfPortals } from '@atlassian/list-of-portals-element';
import { ListOfTopics } from '@atlassian/list-of-topics-element';
import { NoContent } from '@atlassian/no-content-element';
import { SuggestedRequestForms } from '@atlassian/suggested-request-forms-element';
import type { elementViewRendererFragment$key } from './__generated__/elementViewRendererFragment.graphql';

interface ElementProps {
    element: elementViewRendererFragment$key;
    parentSubsectionId: string;
}

const ElementViewRenderer = ({ element: elementKey }: ElementProps) => {
    di(EditorViewerComponent, HeroElement, HelpCenterAnnouncement, ListOfTopics, ListOfPortals, SuggestedRequestForms);
    const elementData = useFragment(
        graphql`
            fragment elementViewRendererFragment on HelpLayoutElement {
                __typename
                ... on HelpLayoutEditorElement {
                    # It does not recognize this valid fragment spread since we are using a re-export from the rich-text package's entry point.
                    # eslint-disable-next-line relay/must-colocate-fragment-spreads
                    ...editorViewRendererFragment
                }
                ... on HelpLayoutPortalsListElement {
                    # It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
                    # It does not report an error if we consume this from the module - list-of-portals, directly.
                    # eslint-disable-next-line relay/must-colocate-fragment-spreads
                    ...listOfPortalsOnHelpCenter_HelpLayoutListOfHelpCenterPortalsAtomicElement
                }
                ... on HelpLayoutSuggestedRequestFormsListElement {
                    # It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
                    # It does not report an error if we consume this from the module - suggested-request-forms, directly.
                    # eslint-disable-next-line relay/must-colocate-fragment-spreads
                    ...suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement
                }
                ... on HelpLayoutAnnouncementElement {
                    # It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
                    # It does not report an error if we consume this from the module - help-center-announcement, directly.
                    # eslint-disable-next-line relay/must-colocate-fragment-spreads
                    ...helpCenterAnnouncementFragment
                }
                ... on HelpLayoutTopicsListElement {
                    ...listOfTopicsElementFragment
                }
                ... on HelpLayoutHeroElement {
                    # It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
                    # It does not report an error if we consume this from the module - help-center-hero-element, directly.
                    # eslint-disable-next-line relay/must-colocate-fragment-spreads
                    ...heroElementFragment
                }
                ... on HelpLayoutNoContentElement {
                    __typename
                }
                ... on HelpLayoutForgeElement {
                    __typename
                    # eslint-disable-next-line relay/must-colocate-fragment-spreads
                    ...forgeElementFragment
                }
                ... on HelpLayoutConnectElement {
                    __typename
                    # eslint-disable-next-line relay/must-colocate-fragment-spreads
                    ...connectElementFragment
                }
                ... on QueryError {
                    identifier
                }
            }
        `,
        elementKey
    );

    switch (elementData.__typename) {
        case 'HelpLayoutEditorElement': {
            return <EditorViewerComponent editorElement={elementData} />;
        }
        case 'HelpLayoutHeroElement': {
            return <HeroElement heroElement={elementData} />;
        }
        case 'HelpLayoutAnnouncementElement': {
            return <HelpCenterAnnouncement announcementElement={elementData} />;
        }
        case 'HelpLayoutTopicsListElement': {
            return <ListOfTopics topicsElement={elementData} />;
        }
        case 'HelpLayoutPortalsListElement': {
            return <ListOfPortals listOfHelpCenters={elementData} />;
        }
        case 'HelpLayoutSuggestedRequestFormsListElement': {
            return <SuggestedRequestForms forms={elementData} />;
        }
        case 'HelpLayoutForgeElement': {
            return isAppsInFlexibleHCEnabled() ? <ForgeElement forgeElementRef={elementData} /> : null;
        }
        case 'HelpLayoutConnectElement': {
            return isAppsInFlexibleHCEnabled() ? <ConnectElement connectElementRef={elementData} /> : null;
        }
        case 'HelpLayoutNoContentElement': {
            return <NoContent />;
        }
        case 'QueryError': {
            const identifier = (elementData as unknown as { identifier: string }).identifier || 'Element';
            throw new Error(`${identifier} content failed to load`);
        }
        default: {
            return null;
        }
    }
};

export default ElementViewRenderer;
